'use client';

import Image from 'next/image';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { VariantProps } from 'tailwind-variants';

import { IconKitMinus } from '@/icon/kit/Minus';
import { IconKitPlus } from '@/icon/kit/Plus';
import { type ButtonColor } from '@/ui-kit/button';
import { UiKitCollapse } from '@/ui-kit/collapse';
import { UiToolScrollDraggable } from '@/ui-tool/scroll-draggable';
import { utilScrollToElement } from '@/util/scroll-to-element';

import { styles } from './UiKitPanel.styles';

type Variants = VariantProps<typeof styles>;

export interface UiKitPanelProps {
  activeIndex?: number;
  className?: string;
  tabsClassName?: string;
  tabClassName?: string;
  tabAdditionalTabClassName?: string;
  contentClassName?: string;
  tabsWrapClassName?: string;
  tabActiveColor?: ButtonColor;
  view?: Variants['view'];
  items: {
    header: React.ReactNode;
    headerIcon?: {
      url: string;
      alt?: string;
      width?: number;
      height?: number;
    };
    content: React.ReactNode;
    disabled?: boolean;
  }[];
  onTabChange?: (index: number) => void;
}

export function UiKitPanel({
  activeIndex: activeIndexProp = 0,
  view = 'auto',
  items,
  className,
  tabsClassName,
  tabClassName,
  tabAdditionalTabClassName,
  contentClassName,
  tabsWrapClassName,
  onTabChange
}: UiKitPanelProps) {
  const [activeIndex, setActiveIndex] = useState(activeIndexProp);
  const headerRefs = useRef<HTMLDivElement[]>([]);
  const contentRefs = useRef<HTMLDivElement[]>([]);
  const slots = styles({ view });
  const scrollTargetRef = useRef<HTMLDivElement | null>(null);

  const setHeaderRef = useCallback(
    (index: number) => (element: HTMLDivElement | null) => {
      if (element) {
        headerRefs.current[index] = element;
      }
    },
    []
  );

  const setContentRef = useCallback(
    (index: number) => (element: HTMLDivElement | null) => {
      if (element) {
        contentRefs.current[index] = element;
      }
    },
    []
  );

  useEffect(() => {
    setActiveIndex(activeIndexProp);
  }, [activeIndexProp]);

  const handleClick = (index: number) => () => {
    setActiveIndex(index);
    onTabChange?.(index);

    if (view === 'auto') {
      scrollTargetRef.current = contentRefs.current[index];
    }
  };

  return (
    <div className={slots.base({ className })}>
      <UiToolScrollDraggable className={slots.tabs({ className: tabsClassName })}>
        <div className={slots.tabsWrap({ className: tabsWrapClassName })}>
          {items.map((item, index) => (
            <Fragment key={index}>
              {view !== 'additionalTab' && (
                <button
                  onClick={handleClick(index)}
                  disabled={item.disabled}
                  className={slots.tab({
                    active: activeIndex === index,
                    className: tabClassName
                  })}
                >
                  {item.header}
                  {item.headerIcon && (
                    <Image
                      className={slots.tabHeaderIcon({
                        active: activeIndex === index
                      })}
                      src={item.headerIcon.url}
                      alt={item.headerIcon.alt ?? ''}
                      width={item.headerIcon.width}
                      height={item.headerIcon.height}
                    />
                  )}
                </button>
              )}
              {view === 'additionalTab' && (
                <button
                  type="button"
                  className={slots.additionalTab({
                    active: activeIndex === index,
                    className: tabAdditionalTabClassName
                  })}
                  data-active={activeIndex === index}
                  data-text={item.header}
                  onClick={handleClick(index)}
                >
                  {item.header}
                  {item.headerIcon && (
                    <Image
                      className={slots.tabHeaderIcon({
                        active: activeIndex === index
                      })}
                      src={item.headerIcon.url}
                      alt={item.headerIcon.alt ?? ''}
                      width={item.headerIcon.width}
                      height={item.headerIcon.height}
                    />
                  )}
                </button>
              )}
            </Fragment>
          ))}
        </div>
      </UiToolScrollDraggable>

      {items.map((item, index) => (
        <Fragment key={index}>
          <div
            ref={setHeaderRef(index)}
            className={slots.header({
              active: activeIndex === index
            })}
            onClick={() => {
              setActiveIndex(index);

              if (view === 'auto') {
                scrollTargetRef.current = headerRefs.current[index];
              }
            }}
          >
            {item.header}
            {item.headerIcon && (
              <Image
                className={slots.tabHeaderIcon({
                  active: activeIndex === index
                })}
                src={item.headerIcon.url}
                alt={item.headerIcon.alt ?? ''}
                width={item.headerIcon.width}
                height={item.headerIcon.height}
              />
            )}
            <span className={slots.headerIcon()}>
              {activeIndex === index && <IconKitMinus />}
              {activeIndex !== index && <IconKitPlus />}
            </span>
          </div>
          <UiKitCollapse
            className={slots.wrapper({ className: contentClassName })}
            collapse={activeIndex !== index}
            onTransitionEnd={() => {
              if (scrollTargetRef.current) {
                utilScrollToElement(scrollTargetRef.current);
                scrollTargetRef.current = null;
              }
            }}
          >
            <div
              ref={setContentRef(index)}
              className={slots.content({
                active: activeIndex === index
              })}
            >
              {item.content}
            </div>
          </UiKitCollapse>
        </Fragment>
      ))}
    </div>
  );
}
